import { Component, DestroyRef, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { UtilService } from '../../../services/util.service';
import { API, PlayerSoftwareDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { NavigationService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-player-software-edit',
  templateUrl: './player-software-edit.component.html',
  styleUrls: ['./player-software-edit.component.scss'],
  standalone: false,
})
export class PlayerSoftwareEditComponent implements OnInit {
  loading: boolean = true;
  lastRequest: number;
  playerSoftware: PlayerSoftwareDTO;
  playerSoftwareId: number;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    OS: new UntypedFormControl('', [Validators.required]),
    Version: new UntypedFormControl('', [Validators.required]),
    Protocol: new UntypedFormControl('', [Validators.required]),
    Description: new UntypedFormControl(''),
  });

  get Name() {
    return this.form.get('Name');
  }

  get OS() {
    return this.form.get('OS');
  }

  get Version() {
    return this.form.get('Version');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Protocol() {
    return this.form.get('Protocol');
  }

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    private navService: NavigationService,
    public translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.lastRequest = -1;
    this.playerSoftwareId = undefined;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.playerSoftwareId = +params.playerSoftwareID;
      this.refreshFromServer();
    });

    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.playerSoftware.name = value;
    });

    this.OS.valueChanges.subscribe(value => {
      this.playerSoftware.os = value;
    });

    this.Version.valueChanges.subscribe(value => {
      this.playerSoftware.version = value;
    });

    this.Protocol.valueChanges.subscribe(value => {
      this.playerSoftware.protocol = value;
    });

    this.Description.valueChanges.subscribe(value => {
      this.playerSoftware.description = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.playerSoftware.name);
    this.Name.markAsTouched();

    this.OS.setValue(this.playerSoftware.os);
    this.OS.markAsTouched();

    this.Version.setValue(this.playerSoftware.version);
    this.Version.markAsTouched();

    this.Description.setValue(this.playerSoftware.description);
    this.Description.markAsTouched();

    this.Protocol.setValue(this.playerSoftware.protocol);
    this.Protocol.markAllAsTouched();
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.playerSoftwareId) {
      this.playerSoftware = new PlayerSoftwareDTO();
      this.loading = false;
    } else {
      this.api
        .ca_md_PlayerSoftware_retrieveById(this.playerSoftwareId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (apiPlayerSoftware: PlayerSoftwareDTO) => {
            this.playerSoftware = apiPlayerSoftware;
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updatePlayerSoftware() {
    this.loading = true;
    if (!this.playerSoftware.id) {
      if (this.playerSoftware.name) {
        this.api
          .ca_md_PlayerSoftware_createPlayerSoftware(this.playerSoftware)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: () => {
              this.navService.navigateToPoint(this.util.RoutingID.playerSoftware);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .ca_md_PlayerSoftware_editPlayerSoftware(this.playerSoftware)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.navService.navigateToPoint(this.util.RoutingID.playerSoftware);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }
}
